'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import { useFocusTrap } from '@/lib/hooks/useFocusTrap'
import { Button } from '@/components/ui/Button'
import type { ExamSearchEntry } from '@/data/exams/search-index'
import { searchExams } from './exam-search'
import { MobileMenuSections } from './MobileMenuSections'
import { MobileSearchResults } from './MobileSearchResults'

interface MobileMenuSheetProps {
  entries: ExamSearchEntry[]
  closing: boolean
  onClose: () => void
}

/**
 * Full-height mobile navigation sheet: search field on top, industry
 * accordions and site links in the scroll area, "Find your exam" pinned to
 * the bottom. Any link click inside closes the sheet (event delegation).
 */
export function MobileMenuSheet({
  entries,
  closing,
  onClose,
}: MobileMenuSheetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState<string>('')

  useFocusTrap(containerRef)

  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [])

  const trimmed = query.trim()
  const results = trimmed ? searchExams(entries, trimmed) : []

  // Portal to body: the nav island's backdrop-blur makes it the containing
  // block for fixed descendants, which would trap this sheet inside the pill.
  return createPortal(
    <div
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a')) onClose()
      }}
      data-closing={closing ? 'true' : undefined}
      className="bg-bg anim-sheet-in data-[closing=true]:anim-sheet-out fixed inset-0 z-[60] flex flex-col outline-none lg:hidden"
    >
      <div className="anim-menu-item border-border flex items-center gap-2 border-b p-3">
        <div className="border-border bg-surface flex h-11 flex-1 items-center gap-2 rounded-xl border px-3">
          <Search size={16} className="text-hint shrink-0" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search your exam or credential"
            aria-label="Search exams"
            className="text-ink placeholder:text-hint h-full w-full bg-transparent text-[15px] outline-none"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="text-ink hover:bg-surface flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors"
        >
          <X size={20} />
        </button>
      </div>
      <p className="sr-only" role="status">
        {trimmed
          ? `${results.length} ${results.length === 1 ? 'exam' : 'exams'} found`
          : ''}
      </p>
      <div className="anim-menu-item flex-1 overflow-y-auto px-3 py-2 [animation-delay:60ms]">
        {trimmed ? (
          <MobileSearchResults results={results} query={trimmed} />
        ) : (
          <MobileMenuSections entries={entries} />
        )}
      </div>
      <div className="anim-menu-item border-border border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] [animation-delay:120ms]">
        <Button href="/exams" size="lg" className="w-full">
          Find your exam
        </Button>
      </div>
    </div>,
    document.body
  )
}
